"use client";
import { useBeforeGtmEvent } from "@/app/_helpers/Events";
import { Translate } from "@/components/Translate";
import { AutoAnimate } from "@/components/ui/AutoAnimate";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/Input";
import { withSearchParams } from "@/components/withSearchParams";
import { useTranslations } from "@/i18n/client";
import type { StoryblokRichtext } from "@/storyblok-base";
import { useRegister, useSubscribe } from "@frend-digital/centra/client";
import { type GA4Event, sendGTMEvent } from "@frend-digital/events/gtm";
import { zodResolver } from "@hookform/resolvers/zod";
import { type PropsWithChildren, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { useAccountModal } from "../AccountModal";
import { ErrorText, Link, Title } from "../Components";
import styles from "../Login/Login.module.css";
import { NewsletterCheckbox } from "../NewsletterCheckbox";
import { ACCOUNT_PATHS } from "../config";
import { useAccountNavigate } from "../hooks";

import { useGeo } from "@/i18n/routing";
import { useFlag } from "@/lib/flags/client";
import { SelectField } from "@/components/ui/SelectField";
import { rpc } from "#rpc";

const COUNTRY_OPTIONS = [
	{ value: "NO", label: "Norway" },
	{ value: "SE", label: "Sweden" },
	{ value: "DK", label: "Denmark" },
	{ value: "FI", label: "Finland" },
	{ value: "EE", label: "Estonia" },
	{ value: "DE", label: "Germany" },
	{ value: "AT", label: "Austria" },
];

export type RegisterFormProps = {
	modal?: boolean;
	text?: string;
	signUpTerms?: StoryblokRichtext | undefined;
	signupLabel?: string;
};

type RegisterError = {
	token?: string;
	errors?: {
		email?: string;
		[key: string]: string | undefined;
	};
};

export function isRegisterError(error: unknown): error is RegisterError {
	return (
		typeof error === "object" &&
		error !== null &&
		"errors" in error &&
		typeof error.errors === "object"
	);
}

export const RegisterForm = withSearchParams<RegisterFormProps>(
	({ modal, signUpTerms, signupLabel, searchParams }) => {
		const t = useTranslations();
		const register = useRegister();
		const [, setOpen] = useAccountModal();
		const subscribe = useSubscribe();
		const step = (searchParams.get("step") || "email") as "email" | "full" | null;
		const beforeEvent = useBeforeGtmEvent();
		const { country, language } = useGeo();
		const loyaltyEnabled = useFlag("loyalty.enabled");

		const [pending, navigate] = useAccountNavigate({ modal, showAccountCreated: true });
		const schema = useMemo(
			() =>
				z
					.object({
						firstName: z.string().min(1, { message: t("First name is required") }),
						lastName: z.string().min(1, { message: t("Last name is required") }),
						email: z.string().email({ message: t("Invalid email") }),
						password: z.string().min(8, { message: t("Password must be at least 8 characters") }),
						confirmPassword: z.string().min(8, { message: t("Password is required") }),
						country: z.string().min(1, { message: t("Country is required") }),
						newsletter: z.boolean(),
					})
					.refine((data) => data.password === data.confirmPassword, {
						message: t("Passwords must match"),
						path: ["confirmPassword"],
					}),
			[t],
		);

		const form = useForm<z.infer<typeof schema>>({
			resolver: zodResolver(schema),
			defaultValues: {
				firstName: "",
				lastName: "",
				email: "",
				password: "",
				confirmPassword: "",
				country: country ?? "NO",
				newsletter: false,
			},

			mode: "onSubmit",
			reValidateMode: "onChange",
		});

		const handleSubmit = ({ confirmPassword: _, newsletter, country: selectedCountry, ...data }: z.infer<typeof schema>) => {
			// Set sessionStorage flag BEFORE registration to survive any reloads (for both modal and page)
			if (typeof window !== "undefined") {
				sessionStorage.setItem("accountCreated", "true");
			}

			register.mutate({ ...data, country: selectedCountry }, {
				onSuccess: async () => {
					if (newsletter) {
						subscribe.mutate({ email: data.email });
					}

					// Update Klaviyo profile with loyalty attributes for all users
					try {
						await rpc.klaviyo.updateProfile({
							attributes: {
								email: data.email,
								first_name: data.firstName,
								last_name: data.lastName,
								properties: {
									loyalty_user: loyaltyEnabled,
									loyalty_market: loyaltyEnabled ? selectedCountry : undefined,
									browsing_locale: `${language}_${selectedCountry}`,
									country: COUNTRY_OPTIONS.find((o) => o.value === selectedCountry)?.label ?? selectedCountry,
								},
							},
						});
					} catch (error) {
						console.error("Failed to update Klaviyo loyalty attributes:", error);
					}

					if (beforeEvent) {
						sendGTMEvent(
							await beforeEvent({
								// biome-ignore lint/suspicious/noExplicitAny: The GA4Event type does not strictly match the event string; using 'any' is necessary for compatibility with the analytics library.
								event: "sign_up" as any,
								method: "email",
							} as unknown as GA4Event),
						);
					}

					navigate();
				},
				onError: () => {
					// Clear sessionStorage flag on error
					if (typeof window !== "undefined") {
						sessionStorage.removeItem("accountCreated");
					}
				},
			});
		};

		return (
			<FormProvider {...form}>
				<AutoAnimate asChild>
					<form className={styles.form} onSubmit={form.handleSubmit(handleSubmit)}>
						{!modal && (
							<Title>
								<Translate>Register</Translate>
							</Title>
						)}
						{register.error && (
							<ErrorText>
								{isRegisterError(register.error) && register.error.errors?.email ? (
									<Translate>{register.error.errors?.email}</Translate>
								) : (
									<Translate>Invalid credentials</Translate>
								)}
							</ErrorText>
						)}
						<FormField
							name="email"
							placeholder={t("Enter your email-address")}
							label={<Translate>Email</Translate>}
						/>

						{step === "full" && (
							<>
								<FormField name="firstName" label={<Translate>First Name</Translate>} />
								<FormField name="lastName" label={<Translate>Last Name</Translate>} />
								<SelectField
									name="country"
									label={<Translate>Country</Translate>}
									options={COUNTRY_OPTIONS}
									placeholder={t("Select a country")}
								/>
								<FormField
									type="password"
									name="password"
									label={<Translate>Password</Translate>}
								/>
								<FormField
									type="password"
									name="confirmPassword"
									label={<Translate>Confirm password</Translate>}
								/>
								<NewsletterCheckbox signupTerms={signUpTerms} signupLabel={signupLabel} />
							</>
						)}

						{step === "email" && (
							<Button
								onClick={() => {
									form
										.trigger("email")
										.then((isValid) => {
											if (isValid) searchParams.set("step", "full");
										})
										.catch(() => {});
								}}
								color="red"
								type="button"
								className={styles.mobileButton}
							>
								<Translate>Continue to details</Translate>
							</Button>
						)}

						{step === "full" && (
							<Button color="red" type="submit" loading={register.isPending || pending}>
								<Translate>Register</Translate>
							</Button>
						)}
						{!modal ? (
							<Link href={ACCOUNT_PATHS.LOGIN}>
								<Translate>Already have an account? Login.</Translate>
							</Link>
						) : (
							<button className={styles.link} type="button" onClick={() => setOpen(true, "login")}>
								<Translate>Already have an account? Login.</Translate>
							</button>
						)}
					</form>
				</AutoAnimate>
			</FormProvider>
		);
	},
);

export const RegisterContentArea = withSearchParams<PropsWithChildren>(
	({ children, searchParams }) => {
		if (searchParams.get("step") === "full") {
			return null;
		}
		return <>{children}</>;
	},
);
